import {FormControl, FormField} from 'platform/components';
import {HStack, Show} from 'platform/foundation';

import {FieldPathByValue, FieldValues} from 'react-hook-form';

import i18n from '@dms/i18n';
import {getLevelIndicatorFlags, LevelIndicator} from '@dms/shared';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {FUEL_LEVEL_STEP_PERCENTAGE} from '../../constants/fuelLevelStepPercentage';

/** What both gauges bind to — the same path type `FormField` computes for these two control types. */
type LevelFieldPath<TFieldValues extends FieldValues> = FieldPathByValue<
  TFieldValues,
  number | null | Nullish
>;

type VehicleLevelFieldsProps<TFieldValues extends FieldValues> = {
  control: FormControl<TFieldValues>;
  fuelTankName: LevelFieldPath<TFieldValues>;
  batteryChargeName: LevelFieldPath<TFieldValues>;
  /** From `levelIndicatorsFor(vehicle.fuelType)` — electric, hybrid and combustion differ. */
  levelIndicators: LevelIndicator[];
  isDisabled?: boolean;
  isFuelRequired?: boolean;
  isBatteryRequired?: boolean;
} & TestIdProps;

/**
 * The level gauges a vehicle's fuel type calls for: a fuel tank, a battery charge, or both
 * side by side for a hybrid. The fuel tank is a percentage here and a 0–1 ratio in the request
 * body; the battery charge is a whole percentage throughout.
 *
 * Both field names are props so each caller resolves its own literal against its own form type —
 * a form missing either field then fails to compile instead of registering a path that silently
 * never reaches the request body.
 */
export function VehicleLevelFields<TFieldValues extends FieldValues>(
  props: VehicleLevelFieldsProps<TFieldValues>
) {
  const {hasFuel, hasBattery} = getLevelIndicatorFlags(props.levelIndicators);

  return (
    <HStack spacing={4} align="flex-end">
      <Show when={hasFuel}>
        <FormField
          control={props.control}
          type="fuelLevel"
          step={FUEL_LEVEL_STEP_PERCENTAGE}
          name={props.fuelTankName}
          label={i18n.t('entity.workshop.labels.fuelTank')}
          isDisabled={props.isDisabled}
          isRequired={props.isFuelRequired}
          data-testid={suffixTestId('fuelTank', props)}
        />
      </Show>
      <Show when={hasBattery}>
        <FormField
          control={props.control}
          type="batteryCharge"
          name={props.batteryChargeName}
          label={i18n.t('entity.vehicle.labels.batteryCharge')}
          isDisabled={props.isDisabled}
          isRequired={props.isBatteryRequired}
          data-testid={suffixTestId('batteryCharge', props)}
        />
      </Show>
    </HStack>
  );
}
